/**
 * Helpdesk rules shared by the API and the worker (ADR 0028). Pure and RN-safe.
 *
 * One implementation of the lifecycle, so the ticket screen and the queue cannot disagree. SLA
 * arithmetic (`addWorkingMinutes`, `workingMinutesBetween`, `resumeDueAt`) lives in
 * `business-calendar.ts`, shared with the travel desk.
 */

import type { BusinessCalendar } from './business-calendar.js';
import type { HelpdeskPriority, HelpdeskTicketStatus } from './enums.js';

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * The moves an agent may make. Reopening a resolved or closed ticket is its own action (with a
 * window), and `closed` is final otherwise — a closed ticket is history, not a queue item.
 */
export const HELPDESK_TRANSITIONS: Readonly<
  Record<HelpdeskTicketStatus, readonly HelpdeskTicketStatus[]>
> = Object.freeze({
  open: ['in_progress', 'waiting_on_employee', 'resolved', 'closed'],
  in_progress: ['open', 'waiting_on_employee', 'resolved', 'closed'],
  waiting_on_employee: ['in_progress', 'resolved', 'closed'],
  resolved: ['closed'],
  closed: [],
});

export function canMoveHelpdeskTicket(
  from: HelpdeskTicketStatus,
  to: HelpdeskTicketStatus,
): boolean {
  return HELPDESK_TRANSITIONS[from].includes(to);
}

/** Still in someone's queue. */
export function isActiveHelpdeskStatus(status: HelpdeskTicketStatus): boolean {
  return status === 'open' || status === 'in_progress' || status === 'waiting_on_employee';
}

const DAY_MS = 86_400_000;

/** May a resolved or closed ticket still be reopened at `now`? */
export function withinReopenWindow(
  resolvedAt: Date | null,
  now: Date,
  reopenWindowDays: number,
): boolean {
  if (!resolvedAt) return false;
  return now.getTime() <= resolvedAt.getTime() + reopenWindowDays * DAY_MS;
}

export function formatHelpdeskTicketNo(n: number): string {
  return `HD-${String(n).padStart(6, '0')}`;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/** Seeded once per organisation. HR and Payroll are confidential out of the box. */
export const HELPDESK_DEFAULT_CATEGORIES: readonly {
  readonly name: string;
  readonly description: string;
  readonly isConfidential: boolean;
}[] = [
  { name: 'IT', description: 'Laptops, access, accounts and software', isConfidential: false },
  { name: 'HR', description: 'Policies, records and personal matters', isConfidential: true },
  { name: 'Payroll', description: 'Pay, tax and reimbursements', isConfidential: true },
  { name: 'Facilities', description: 'Office, seating, travel desk', isConfidential: false },
  { name: 'Other', description: 'Anything else', isConfidential: false },
];

/** Working minutes. A 9-hour working day: urgent = same day, low = two weeks to resolve. */
export const HELPDESK_DEFAULT_SLA_MINUTES: Readonly<
  Record<HelpdeskPriority, { readonly firstResponse: number; readonly resolution: number }>
> = Object.freeze({
  urgent: { firstResponse: 60, resolution: 540 },
  high: { firstResponse: 240, resolution: 1_080 },
  medium: { firstResponse: 540, resolution: 2_700 },
  low: { firstResponse: 1_080, resolution: 5_400 },
});

// ---------------------------------------------------------------------------
// SLA arithmetic
// ---------------------------------------------------------------------------

/**
 * A location's working time for SLA clocks. The arithmetic over it is in `business-calendar.ts`;
 * this name stays so helpdesk callers read naturally.
 */
export type HelpdeskBusinessCalendar = BusinessCalendar;
